import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { xhrRequest } from '../lib/xhrRequest';
import { toast } from 'sonner';
import { Loader2, CreditCard, CheckCircle, ArrowRight, AlertCircle, ChevronDown, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

// Map raw Stripe requirement field paths to friendly, human-readable labels.
// Any field not in the map falls back to a title-cased version of the last
// path segment (e.g. `individual.verification.document` → "Verification document").
const REQUIREMENT_LABELS = {
  'individual.first_name': 'Legal first name',
  'individual.last_name': 'Legal last name',
  'individual.dob.day': 'Date of birth',
  'individual.dob.month': 'Date of birth',
  'individual.dob.year': 'Date of birth',
  'individual.email': 'Email address',
  'individual.phone': 'Phone number',
  'individual.address.line1': 'Home address',
  'individual.address.city': 'City',
  'individual.address.state': 'State',
  'individual.address.postal_code': 'Postal code',
  'individual.ssn_last_4': 'Last 4 of Social Security number',
  'individual.id_number': 'Government ID number',
  'individual.verification.document': 'Photo ID (front & back)',
  'individual.verification.additional_document': 'Additional ID document',
  'external_account': 'Bank or Cash App account info',
  'tos_acceptance.date': 'Terms of Service acceptance',
  'tos_acceptance.ip': 'Terms of Service acceptance',
  'business_profile.mcc': 'Category',
  'business_profile.url': 'Website',
  'business_profile.product_description': 'What you do',
};

const friendlyLabel = (path) => {
  if (REQUIREMENT_LABELS[path]) return REQUIREMENT_LABELS[path];
  const last = path.split('.').pop() || path;
  return last
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
};

// Deduplicate: some Stripe fields collapse to the same friendly label
// (e.g. dob.day/month/year → "Date of birth"). We only want each label once.
const uniqueLabels = (paths) => {
  const seen = new Set();
  const out = [];
  for (const p of paths || []) {
    const l = friendlyLabel(p);
    if (!seen.has(l)) {
      seen.add(l);
      out.push(l);
    }
  }
  return out;
};

export default function OnboardingPayoutsPage() {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  // statusDetails is what Stripe says is still required.
  // We display it back to the worker so they know exactly what to fix.
  const [statusDetails, setStatusDetails] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Stripe AccountLink expires after one use OR 5 minutes since creation.
  // If the worker comes back via Stripe's `refresh_url` (?refresh=true),
  // we generate a fresh link automatically and bounce them straight back
  // into onboarding instead of making them re-click "Connect".
  const regenerateAccountLink = async () => {
    try {
      const { ok, data } = await xhrRequest(
        'POST',
        `${API}/stripe/create-connect-account`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ originUrl: window.location.origin }),
        }
      );
      if (!ok || !data.url) {
        toast.error(data.detail || 'Could not refresh Stripe onboarding link.');
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message || 'Could not refresh Stripe onboarding link.');
    }
  };

  useEffect(() => {
    const success = searchParams.get('success');
    const refresh = searchParams.get('refresh');

    if (success !== 'true' && refresh !== 'true') return;

    if (refresh === 'true') {
      toast.info("Resuming Stripe onboarding…");
      regenerateAccountLink();
      return;
    }

    let cancelled = false;
    const checkAccountStatus = async () => {
      setChecking(true);
      try {
        const { data } = await xhrRequest('POST', `${API}/stripe/check-account-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (cancelled) return;

        if (data.onboarded) {
          await refreshUser();
          toast.success('Payouts connected successfully!');
          navigate('/dashboard');
          return;
        }

        // Not onboarded. Surface Stripe's actual reason so the worker
        // can act on it instead of seeing a generic "incomplete" message.
        setStatusDetails({
          payoutsEnabled: data.payoutsEnabled,
          detailsSubmitted: data.detailsSubmitted,
          transfersCapability: data.transfersCapability,
          requirements: data.requirements || {},
        });

        const due = (data.requirements?.currentlyDue || []).concat(
          data.requirements?.pastDue || []
        );
        if (data.requirements?.disabledReason) {
          toast.error(`Stripe: ${data.requirements.disabledReason}`);
        } else if (due.length > 0) {
          toast.info(`Stripe still needs: ${due.slice(0, 3).join(', ')}${due.length > 3 ? '…' : ''}`);
        } else if ((data.requirements?.pendingVerification || []).length > 0) {
          toast.info("Stripe is reviewing your documents. This usually takes a few minutes.");
        } else {
          toast.info('Account setup incomplete. Please complete all required fields.');
        }
      } catch (error) {
        if (!cancelled) console.error('Status check error:', error);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    checkAccountStatus();
    return () => { cancelled = true; };
  }, [searchParams, API, token, navigate, refreshUser]);

  const handleConnectStripe = async () => {
    if (loading) return; // guard against rapid double-click
    setLoading(true);
    try {
      const { ok, status, data } = await xhrRequest(
        'POST',
        `${API}/stripe/create-connect-account`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ originUrl: window.location.origin }),
        }
      );

      if (!ok) {
        throw new Error(data.detail || `Failed to connect Stripe (${status})`);
      }
      if (!data.url) {
        throw new Error('Stripe did not return an onboarding URL. Please try again.');
      }

      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast.info('You can connect payouts later from your dashboard');
    navigate('/dashboard');
  };

  const isConnected = user?.stripeOnboarded;

  return (
    <div className="min-h-screen flex flex-col bg-[#050505]">
      <div className="auth-container flex-1">
      <div className="auth-card animate-fade-in max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-full bg-[#F5C542]/20 text-[#F5C542] flex items-center justify-center font-bold text-sm">
              <CheckCircle size={16} />
            </span>
            <div className="w-16 h-0.5 bg-[#F5C542]" />
            <span className="w-8 h-8 rounded-full bg-[#F5C542] text-black flex items-center justify-center font-bold text-sm">2</span>
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">Connect Payouts</h1>
          <p className="text-[#A1A1AA]">Link your bank or debit card to receive tips</p>
        </div>

        {isConnected ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Payouts Connected!</h2>
            <p className="text-[#A1A1AA] mb-6">You&apos;re all set to receive tips.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary flex items-center justify-center gap-2 mx-auto"
              data-testid="go-to-dashboard-button"
            >
              Go to Dashboard
              <ArrowRight size={20} />
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="bg-white/5 rounded-xl p-5 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F5C542]/10 flex items-center justify-center flex-shrink-0">
                  <CreditCard size={24} className="text-[#F5C542]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Stripe Connect</h3>
                  <p className="text-sm text-[#A1A1AA]">
                    Secure payment processing. Tips land in your account minus a 3% platform fee.
                    Opt into the{' '}
                    <Link to="/terms#challenge-rules" className="text-[#F5C542] hover:underline">
                      Top Earner Challenge
                    </Link>{' '}
                    to compete for monthly prize pools (participants pay an additional 1% during enrolled periods).
                  </p>
                </div>
              </div>
            </div>

            {/* Plain-language status panel — replaces the old raw-field
                developer view. Only shown when the worker has come back
                from Stripe but isn't fully verified. */}
            {statusDetails && !statusDetails.payoutsEnabled && (
              <div
                className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30 text-sm space-y-3"
                data-testid="stripe-status-panel"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-100">
                      You&apos;re almost done
                    </p>
                    <p className="text-amber-200/80 mt-1">
                      Stripe still needs a few details to verify your identity before you can get paid out. Continue below to finish.
                    </p>
                  </div>
                </div>

                {(() => {
                  const due = uniqueLabels([
                    ...(statusDetails.requirements.currentlyDue || []),
                    ...(statusDetails.requirements.pastDue || []),
                  ]);
                  if (due.length === 0) return null;
                  return (
                    <div className="pl-8">
                      <p className="text-amber-200/70 mb-1.5">Still needed:</p>
                      <ul className="list-disc list-inside text-amber-100/90 space-y-0.5">
                        {due.map((label) => (
                          <li key={label}>{label}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}

                {statusDetails.requirements.pendingVerification?.length > 0 && (
                  <p className="pl-8 text-amber-200/70">
                    Stripe is reviewing your documents. This usually takes a few minutes.
                  </p>
                )}
              </div>
            )}

            {/* Cash App helper — collapsible so it doesn't compete with
                the primary CTA. Many service workers don't have a
                traditional bank; showing them the Cash App path unblocks
                onboarding. */}
            <details className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-testid="cashapp-helper">
              <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 hover:bg-white/[0.03] transition-colors">
                <span className="flex items-center gap-2 text-sm">
                  <Info size={16} className="text-[#F5C542] flex-shrink-0" />
                  Don&apos;t have a traditional bank account?
                </span>
                <ChevronDown size={16} className="text-[#A1A1AA] transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 pt-1 text-sm text-[#A1A1AA] leading-relaxed">
                You can still use Cash App. Open Cash App, go to{' '}
                <span className="text-white">Money → Direct Deposit → Account Details</span>, and enter that account and routing number when Stripe asks for your bank info. It works the same way.
              </div>
            </details>

            {/* Single primary CTA — replaces the previous stack of
                "Resume", "Connect with Stripe", and "Skip for now". */}
            <button
              onClick={handleConnectStripe}
              disabled={loading || checking}
              className="btn-primary flex items-center justify-center gap-2 w-full"
              data-testid="continue-payout-setup-button"
            >
              {loading || checking ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {checking ? 'Checking status…' : 'Opening Stripe…'}
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  {statusDetails && !statusDetails.payoutsEnabled
                    ? 'Continue setup'
                    : 'Connect with Stripe'}
                </>
              )}
            </button>

            <button
              onClick={handleSkip}
              className="btn-secondary w-full"
              data-testid="skip-payouts-button"
            >
              Skip for now
            </button>
          </div>
        )}
      </div>
      </div>
      <Footer />
    </div>
  );
}
